import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PieChart, Wallet, TrendingUp, TrendingDown, Trash2 } from "lucide-react";

export default function PortfolioHoldings({ portfolio, loading, onRemove, onExplore }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
    >
      <Card className="glass-effect">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Unbounded' }}>
            <PieChart className="w-6 h-6 text-[#00E0FF]" />
            HOLDINGS
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading portfolio...</div>
          ) : portfolio.assets.length === 0 ? (
            <div className="text-center py-12">
              <Wallet className="w-16 h-16 mx-auto mb-4 text-gray-600" />
              <p className="text-gray-500 mb-4">Your portfolio is empty</p>
              <Button
                data-testid="explore-assets-btn"
                onClick={onExplore}
                className="bg-[#00E0FF] text-black hover:bg-[#33E9FF] font-semibold"
              >
                Explore Assets
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {portfolio.assets.map((asset, index) => (
                <motion.div
                  key={asset.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  data-testid={`portfolio-asset-${asset.symbol}`}
                  className="glass-effect p-6 rounded-lg hover:border-[#00E0FF]/30 transition-all duration-300"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl font-bold text-white mono">{asset.symbol}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${asset.asset_type === 'crypto' ? 'bg-[#6366F1]/20 text-[#6366F1]' : 'bg-[#00E0FF]/20 text-[#00E0FF]'}`}>
                          {asset.asset_type.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-400">{asset.name}</p>
                    </div>

                    <div className="grid grid-cols-4 gap-8 flex-1">
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Quantity</div>
                        <div className="text-lg font-semibold text-white mono">{asset.quantity}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Buy Price</div>
                        <div className="text-lg font-semibold text-white mono">${asset.buy_price.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Current Price</div>
                        <div className="text-lg font-semibold text-white mono">${asset.current_price.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">P/L</div>
                        <div className="flex items-center gap-2">
                          {asset.profit_loss >= 0
                            ? <TrendingUp className="w-4 h-4 text-[#00FFA3]" />
                            : <TrendingDown className="w-4 h-4 text-[#FF3B30]" />}
                          <div>
                            <div className={`text-lg font-semibold mono ${asset.profit_loss >= 0 ? 'text-[#00FFA3]' : 'text-[#FF3B30]'}`}>
                              {asset.profit_loss >= 0 ? '+' : ''}${asset.profit_loss.toFixed(2)}
                            </div>
                            <div className={`text-xs mono ${asset.profit_loss_percent >= 0 ? 'text-[#00FFA3]' : 'text-[#FF3B30]'}`}>
                              {asset.profit_loss_percent >= 0 ? '+' : ''}{asset.profit_loss_percent.toFixed(2)}%
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <Button
                      data-testid={`remove-asset-${asset.symbol}-btn`}
                      variant="ghost"
                      size="sm"
                      onClick={() => onRemove(asset.id)}
                      className="text-gray-500 hover:text-[#FF3B30] hover:bg-[#FF3B30]/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
